import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import RemoveShoppingCartRoundedIcon from "@mui/icons-material/RemoveShoppingCartRounded";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import PendingActionsRoundedIcon from "@mui/icons-material/PendingActionsRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import MarkEmailReadRoundedIcon from "@mui/icons-material/MarkEmailReadRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import MoneyOffRoundedIcon from "@mui/icons-material/MoneyOffRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import { SvgIconComponent } from "@mui/icons-material";
import { gridClasses, GridColDef } from "@mui/x-data-grid";
import moment from "moment";
import { orderStatuses, paymentStatuses, shippingStatuses } from "../constants/shipping";
import { OrderUserType } from "../constants/order";
import { getSellerPayoutBreakdown } from "../components/utils/utils";
import { OrderItemActions, orderActionButtonPrimarySx } from "./Orders/Sales/OrderItemActions";

const INK = "#1E293B";
const INK_SOFT = "#475569";
const LINE = "#E2E8F0";
const LINE_SOFT = "#F1F5F9";
const BODY_FONT = '"Source Sans 3", sans-serif';
const MONO_FONT = '"Spline Sans Mono", monospace';

export type HistoryGridMode = "buyer" | "seller";

export type HistoryStatusIconKey =
  | "canceled"
  | "out_of_stock"
  | "payment_pending"
  | "paid"
  | "payment_failed"
  | "confirmation_pending"
  | "shipping_pending"
  | "shipped"
  | "shipping_failed"
  | "delivered"
  | "buyer_confirmed"
  | "unknown";

export type HistoryGridRow = {
  id: string;
  orderNumber: string;
  createdAt: string;
  shippingMethod: string;
  counterpartyName: string;
  statusLabel: string;
  statusColor: string;
  statusBg: string;
  statusIcon: HistoryStatusIconKey;
  trackingNumber: string;
  totalLabel: string;
  orders: any[];
  primaryOrder: any;
};

const HISTORY_STATUS_ICONS: Record<HistoryStatusIconKey, SvgIconComponent> = {
  canceled: CancelRoundedIcon,
  out_of_stock: RemoveShoppingCartRoundedIcon,
  payment_pending: HourglassTopRoundedIcon,
  paid: PaidRoundedIcon,
  payment_failed: MoneyOffRoundedIcon,
  confirmation_pending: PendingActionsRoundedIcon,
  shipping_pending: Inventory2RoundedIcon,
  shipped: LocalShippingRoundedIcon,
  shipping_failed: ErrorRoundedIcon,
  delivered: MarkEmailReadRoundedIcon,
  buyer_confirmed: TaskAltRoundedIcon,
  unknown: HelpOutlineRoundedIcon,
};

export const StatusLabelChip = ({
  label,
  color,
  bg,
  iconKey,
  fitContent = false,
}: {
  label: string;
  color: string;
  bg: string;
  iconKey: HistoryStatusIconKey;
  fitContent?: boolean;
}) => {
  const StatusIcon = HISTORY_STATUS_ICONS[iconKey] || HelpOutlineRoundedIcon;

  return (
    <Box
      title={label}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.6,
        width: fitContent ? "fit-content" : "100%",
        height: 26,
        px: 1,
        borderRadius: "7px",
        boxSizing: "border-box",
        fontSize: 12.5,
        fontWeight: 600,
        fontFamily: BODY_FONT,
        letterSpacing: "0.01em",
        color,
        bgcolor: bg,
        minWidth: 0,
        overflow: fitContent ? "visible" : "hidden",
      }}
    >
      <StatusIcon sx={{ fontSize: 16, flexShrink: 0 }} />
      <Box
        component="span"
        sx={{
          minWidth: 0,
          flex: fitContent ? "0 0 auto" : 1,
          overflow: fitContent ? "visible" : "hidden",
          textOverflow: fitContent ? "clip" : "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Box>
    </Box>
  );
};

const getBuyerTotal = (orders: any[]) => {
  const filteredOrders = orders.filter((order: any) => order.status === 1);
  const cutoffDateForGroundShipment = new Date("Mon Feb 20 2025 23:59:59 GMT+0400");
  let total = 0;
  filteredOrders.forEach((item: any) => {
    const orderDate = new Date(item.createdAt);
    const isNewOrderForGround = orderDate > cutoffDateForGroundShipment;
    const isFedexGround = item.ShippingMethod?.key === "fedex-ground";
    const isShippingPaidBySeller = Boolean(item.isShippingPaidBySeller);
    const shippingFee =
      isShippingPaidBySeller || (isNewOrderForGround && isFedexGround)
        ? 0
        : Number(item.shippingFee) || 0;
    total += Number(item.totalAmount) + shippingFee;
  });
  return total.toFixed(2);
};

const getSellerTotal = (orders: any[]) => {
  const breakdown = getSellerPayoutBreakdown(orders.filter((order: any) => order.status === 1));
  if (!breakdown) return "0.00";
  return breakdown.creditTotal.toFixed(2);
};

export const getHistoryStatusDisplay = (order: any) => {
  if (order?.status === 0 || order?.status === 2) {
    return {
      label: orderStatuses[order.status]?.label || "N/A",
      color: orderStatuses[order.status]?.color || INK_SOFT,
      bg: orderStatuses[order.status]?.bgColor || LINE_SOFT,
      icon: (order.status === 0 ? "canceled" : "out_of_stock") as HistoryStatusIconKey,
    };
  }
  if ((order?.shippingStatus === 5 && order?.paymentStatus === 1) || order?.paymentStatus === 2) {
    const paymentIcon: HistoryStatusIconKey =
      order.paymentStatus === 1
        ? "paid"
        : order.paymentStatus === 2
          ? "payment_failed"
          : "payment_pending";
    return {
      label: paymentStatuses[order.paymentStatus]?.label || "N/A",
      color: paymentStatuses[order.paymentStatus]?.color || INK_SOFT,
      bg: paymentStatuses[order.paymentStatus]?.bgColor || LINE_SOFT,
      icon: paymentIcon,
    };
  }

  const shippingIconByIndex: HistoryStatusIconKey[] = [
    "confirmation_pending",
    "shipping_pending",
    "shipped",
    "shipping_failed",
    "delivered",
    "buyer_confirmed",
  ];

  return {
    label: shippingStatuses[order?.shippingStatus]?.label || "N/A",
    color: shippingStatuses[order?.shippingStatus]?.color || INK_SOFT,
    bg: shippingStatuses[order?.shippingStatus]?.bgColor || LINE_SOFT,
    icon: shippingIconByIndex[order?.shippingStatus] || "unknown",
  };
};

/** Same grouping used by the previous card UI — one grid row per order group. */
export function mapOrdersToHistoryGridRows(
  rawOrders: any[] | undefined | null,
  mode: HistoryGridMode
): HistoryGridRow[] {
  if (!rawOrders?.length) return [];

  const sortedOrders = [...rawOrders].sort((a: any, b: any) => {
    const aOrderNumStr = String(a.orderNumber);
    const bOrderNumStr = String(b.orderNumber);
    const aIsLegacy = aOrderNumStr.length > 8;
    const bIsLegacy = bOrderNumStr.length > 8;

    if (aIsLegacy && bIsLegacy) {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    }
    if (!aIsLegacy && !bIsLegacy) {
      const aNum = Number(aOrderNumStr);
      const bNum = Number(bOrderNumStr);
      if (Number.isNaN(aNum) || Number.isNaN(bNum)) {
        return bOrderNumStr.localeCompare(aOrderNumStr);
      }
      return bNum - aNum;
    }
    return aIsLegacy ? 1 : -1;
  });

  const grouped: Record<string, Record<string, any[]>> = {};

  for (const order of sortedOrders) {
    const orderKey = `o_${order.orderNumber}`;
    const partyId = mode === "buyer" ? order.Seller?.id : order.Buyer?.id;
    const partyStatusKey = `${partyId}_${order.status === 1 ? "a" : order.status === 2 ? "b" : "c"}`;

    if (!grouped[orderKey]) grouped[orderKey] = {};
    if (!grouped[orderKey][partyStatusKey]) grouped[orderKey][partyStatusKey] = [];
    grouped[orderKey][partyStatusKey].push(order);
  }

  const rows: HistoryGridRow[] = [];

  Object.keys(grouped).forEach((orderKey) => {
    const orderNumber = orderKey.startsWith("o_") ? orderKey.slice(2) : orderKey;
    Object.keys(grouped[orderKey])
      .sort()
      .forEach((partyKey) => {
        const orders = grouped[orderKey][partyKey];
        const primaryOrder = orders[0];
        const status = getHistoryStatusDisplay(primaryOrder);
        rows.push({
          id: `${orderKey}_${partyKey}`,
          orderNumber,
          createdAt: primaryOrder?.createdAt,
          shippingMethod: primaryOrder?.ShippingMethod?.name || "N/A",
          counterpartyName:
            mode === "buyer"
              ? primaryOrder?.Seller?.legalName || "—"
              : primaryOrder?.Buyer?.legalName || "—",
          statusLabel: status.label,
          statusColor: status.color,
          statusBg: status.bg,
          statusIcon: status.icon,
          trackingNumber:
            primaryOrder?.status === 0 || primaryOrder?.status === 2
              ? ""
              : primaryOrder?.trackingNumber || "",
          totalLabel: mode === "buyer" ? getBuyerTotal(orders) : getSellerTotal(orders),
          orders,
          primaryOrder,
        });
      });
  });

  return rows;
}

const CopyValue = ({
  value,
  onCopy,
}: {
  value: string;
  onCopy: (value: string) => void;
}) => (
  <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, gap: 0.5 }}>
    <Box
      sx={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        fontFamily: MONO_FONT,
        fontSize: "12px",
        fontWeight: 600,
        color: INK,
      }}
    >
      {value || "—"}
    </Box>
    {!!value && (
      <ContentCopyIcon
        fontSize="small"
        onClick={(event) => {
          event.stopPropagation();
          onCopy(value);
        }}
        sx={{ fontSize: 14, color: INK_SOFT, cursor: "pointer", flexShrink: 0 }}
      />
    )}
  </Box>
);

export function getHistoryGridColumns(options: {
  mode: HistoryGridMode;
  isAdmin?: boolean;
  onCopy: (value: string) => void;
  refetch?: () => void;
  refetchLoading?: boolean;
}): GridColDef[] {
  const { mode, isAdmin = false, onCopy, refetch, refetchLoading } = options;
  const userType = mode === "buyer" ? OrderUserType.buyer : OrderUserType.seller;

  const columns: GridColDef[] = [
    {
      field: "orderNumber",
      headerName: "Order #",
      flex: 0.9,
      minWidth: 96,
      sortable: true,
      disableColumnMenu: true,
      renderCell: (params) => <CopyValue value={params.row.orderNumber} onCopy={onCopy} />,
    },
    {
      field: "createdAt",
      headerName: "Date",
      flex: 0.75,
      minWidth: 92,
      sortable: true,
      disableColumnMenu: true,
      valueGetter: (_value, row) => row.createdAt,
      renderCell: (params) => (
        <Box sx={{ fontSize: 13, color: INK }}>
          {params.row.createdAt ? moment(params.row.createdAt).format("MM/DD/YYYY") : "—"}
        </Box>
      ),
    },
    {
      field: "counterpartyName",
      headerName: mode === "buyer" ? "Seller" : "Buyer",
      flex: 1.1,
      minWidth: 110,
      sortable: true,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: INK,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
            width: "100%",
          }}
        >
          {params.row.counterpartyName}
        </Box>
      ),
    },
    {
      field: "shippingMethod",
      headerName: "Shipping",
      flex: 0.95,
      minWidth: 100,
      sortable: true,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box
          sx={{
            fontSize: 13,
            color: INK_SOFT,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
            width: "100%",
          }}
        >
          {params.row.shippingMethod}
        </Box>
      ),
    },
    {
      field: "statusLabel",
      headerName: "Status",
      flex: 1.45,
      minWidth: 168,
      sortable: true,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ width: "100%", minWidth: 0, display: "flex", alignItems: "center" }}>
          <StatusLabelChip
            label={params.row.statusLabel}
            color={params.row.statusColor}
            bg={params.row.statusBg}
            iconKey={params.row.statusIcon}
          />
        </Box>
      ),
    },
    {
      field: "trackingNumber",
      headerName: "Tracking",
      flex: 1.5,
      minWidth: 168,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) =>
        params.row.trackingNumber ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              minWidth: 0,
              width: "100%",
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
              <CopyValue value={params.row.trackingNumber} onCopy={onCopy} />
            </Box>
            <Button
              size="small"
              variant="outlined"
              disableElevation
              aria-label="Track Order"
              onClick={(event) => {
                event.stopPropagation();
                window.open(
                  `https://www.fedex.com/fedextrack/?trknbr=${params.row.trackingNumber}`,
                  "_blank",
                  "noopener,noreferrer"
                );
              }}
              startIcon={<LocalShippingRoundedIcon />}
              sx={{ ...orderActionButtonPrimarySx, flexShrink: 0 }}
            >
              Track
            </Button>
          </Box>
        ) : (
          <Box sx={{ color: INK_SOFT }}>—</Box>
        ),
    },
  ];

  if (!isAdmin) {
    columns.push({
      field: "totalLabel",
      headerName: mode === "buyer" ? "Total" : "Credit total",
      flex: 0.8,
      minWidth: 88,
      sortable: true,
      disableColumnMenu: true,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Box sx={{ fontFamily: MONO_FONT, fontSize: 12.5, fontWeight: 600, color: INK, width: "100%", textAlign: "right" }}>
          ${params.row.totalLabel}
        </Box>
      ),
    });
  }

  columns.push({
    field: "actions",
    headerName: "Actions",
    flex: 2.2,
    minWidth: 280,
    sortable: false,
    disableColumnMenu: true,
    renderCell: (params) => {
      if (params.row.primaryOrder?.status !== 1) {
        return <Box sx={{ color: INK_SOFT, fontSize: 12 }}>—</Box>;
      }
      return (
        <HistoryRowActions
          row={params.row}
          userType={userType}
          isAdmin={isAdmin}
          refetch={refetch}
          refetchLoading={refetchLoading}
        />
      );
    },
  });

  return columns;
}

const HistoryRowActions = ({
  row,
  userType,
  isAdmin,
  refetch,
  refetchLoading,
}: {
  row: HistoryGridRow;
  userType: OrderUserType.buyer | OrderUserType.seller;
  isAdmin?: boolean;
  refetch?: () => void;
  refetchLoading?: boolean;
}) => (
  <Box
    onClick={(event) => event.stopPropagation()}
    sx={{
      display: "flex",
      alignItems: "center",
      width: "100%",
      minWidth: 0,
      overflow: "hidden",
    }}
  >
    <OrderItemActions
      orders={row.orders}
      item={row.primaryOrder}
      user={userType}
      refetch={refetch}
      refetchLoading={refetchLoading}
      isAdmin={isAdmin}
      asIcons
    />
  </Box>
);

export const historyDataGridSx = {
  border: "none",
  fontFamily: BODY_FONT,
  fontSize: "12.5px",
  color: INK,
  width: "100%",
  [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
    outline: "none",
  },
  [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
    outline: "none",
  },
  "& .MuiDataGrid-main": {
    width: "100%",
  },
  "& .MuiDataGrid-virtualScroller": {
    overflowX: "auto",
  },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#F1F5F9",
    borderBottom: `1px solid ${LINE}`,
    minHeight: "40px !important",
    maxHeight: "40px !important",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontFamily: MONO_FONT,
    fontSize: "10px",
    fontWeight: 600,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: INK,
  },
  "& .MuiDataGrid-cell": {
    display: "flex",
    alignItems: "center",
    borderBottom: `1px solid ${LINE_SOFT}`,
    overflow: "hidden",
    py: 0,
  },
  "& .MuiDataGrid-row": {
    transition: "background-color .15s ease",
  },
  [`& .${gridClasses.row}:hover`]: {
    cursor: "pointer",
    backgroundColor: "#F1F5F9",
  },
  "& .MuiDataGrid-footerContainer": {
    minHeight: 44,
    borderTop: `1px solid ${LINE}`,
    backgroundColor: "#FFFFFF",
  },
};
